import { z } from 'zod';

import type { TFunction } from 'i18next';

// Spec V1.1 §M5 — password policy: ≥ 8 chars, ≥ 1 uppercase, ≥ 1 digit.
const HAS_UPPER = /[A-Z]/;
const HAS_DIGIT = /[0-9]/;

type AuthT = TFunction<['auth']>;

export function passwordSchema(t: AuthT) {
  return z
    .string()
    .min(1, t('auth:validation.passwordRequired'))
    .min(8, t('auth:validation.passwordTooShort'))
    .refine((v) => HAS_UPPER.test(v), t('auth:validation.passwordMissingUpper'))
    .refine((v) => HAS_DIGIT.test(v), t('auth:validation.passwordMissingDigit'));
}

export function emailSchema(t: AuthT) {
  return z
    .string()
    .trim()
    .toLowerCase()
    .min(1, t('auth:validation.emailRequired'))
    .email(t('auth:validation.emailInvalid'));
}

export function signInSchema(t: AuthT) {
  return z.object({
    email: emailSchema(t),
    password: z.string().min(1, t('auth:validation.passwordRequired')),
  });
}
export type SignInValues = z.infer<ReturnType<typeof signInSchema>>;

export function signUpSchema(t: AuthT) {
  return z.object({
    firstName: z
      .string()
      .min(1, t('auth:validation.firstNameRequired'))
      .transform((v) => v.trim()),
    lastName: z
      .string()
      .min(1, t('auth:validation.lastNameRequired'))
      .transform((v) => v.trim()),
    email: emailSchema(t),
    phone: z
      .string()
      .trim()
      .refine((v) => v === '' || /^[+0-9 ()\-.]{6,}$/.test(v), t('auth:validation.phoneInvalid')),
    password: passwordSchema(t),
    terms: z.boolean().refine((v) => v === true, { message: t('auth:validation.termsRequired') }),
  });
}
export type SignUpValues = z.infer<ReturnType<typeof signUpSchema>>;

export function resetRequestSchema(t: AuthT) {
  return z.object({
    email: emailSchema(t),
  });
}
export type ResetRequestValues = z.infer<ReturnType<typeof resetRequestSchema>>;

export function resetConfirmSchema(t: AuthT) {
  return z
    .object({
      newPassword: passwordSchema(t),
      confirmPassword: z.string(),
    })
    .refine((d) => d.newPassword === d.confirmPassword, {
      message: t('auth:validation.passwordsMismatch'),
      path: ['confirmPassword'],
    });
}
export type ResetConfirmValues = z.infer<ReturnType<typeof resetConfirmSchema>>;
